import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Zap, CalendarClock, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useBrandContext } from "@/contexts/brand-context";
import { useAccounts } from "@/hooks/use-accounts";
import { useQuickPost } from "@/hooks/use-calendar";
import { apiFetch } from "@/lib/api-client";
import { optimizeImageForUpload, describeOptimization } from "@/lib/image-optimize";
import { captureVideoPoster } from "@/lib/video-poster";
import { FormatPicker } from "@/components/compose/format-picker";
import { TikTokPrivacyKnob } from "@/components/composers/SmartKnobs";
import { LibraryPickerModal } from "@/components/media/library-picker-modal";
import { assetToMediaKind } from "@/hooks/use-brand-assets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MIN_SCHEDULE_LEAD_MS, MIN_SCHEDULE_LEAD_MINUTES } from "@content-os/config";
import type { SocialPlatform } from "@content-os/config";
import type { PostType, MediaDimensions } from "@content-os/post-types";

// Platforms that require media. INSTAGRAM/TIKTOK/YOUTUBE/PINTEREST won't accept
// a text-only post; FACEBOOK/TWITTER/LINKEDIN/THREADS can.
const MEDIA_REQUIRED: Record<SocialPlatform, "image" | "video" | "any" | null> = {
  INSTAGRAM: "any",
  TIKTOK: "video",
  YOUTUBE: "video",
  PINTEREST: "image",
  FACEBOOK: null,
  TWITTER: null,
  LINKEDIN: null,
  THREADS: null,
  REDDIT: null,
  GOOGLEBUSINESS: "image",
};

export function QuickPostPage() {
  const { t } = useTranslation("compose");
  const { brand } = useBrandContext();
  const navigate = useNavigate();
  const { brandSlug } = useParams<{ brandSlug: string }>();
  const { data: accounts } = useAccounts(brand.id);
  const quickPostMut = useQuickPost(brand.id);

  // Optional prefill (e.g. routed from the calendar's unscheduled queue with a
  // raw upload, and possibly a dropped date). Read once on mount.
  const location = useLocation();
  const prefill = (location.state ?? null) as {
    mediaUrl?: string;
    mediaType?: string;
    scheduledFor?: string;
  } | null;
  const prefillMediaType: "image" | "video" | null = prefill?.mediaType
    ? prefill.mediaType.startsWith("video")
      ? "video"
      : "image"
    : null;
  // The schedule input is a browser-local datetime-local string; convert the
  // (ISO) prefill instant to local wall time so it round-trips on submit.
  const toLocalInput = (iso: string) => {
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const activeAccounts = (accounts ?? []).filter((a) => a.status === "ACTIVE");

  const [caption, setCaption] = useState("");
  const [hashtagsRaw, setHashtagsRaw] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<SocialPlatform>>(new Set());
  const [mediaUrl, setMediaUrl] = useState<string | null>(prefill?.mediaUrl ?? null);
  const [mediaType, setMediaType] = useState<"image" | "video" | null>(prefillMediaType);
  const [mediaDims, setMediaDims] = useState<{ width: number; height: number } | null>(null);
  const [formatByPlatform, setFormatByPlatform] = useState<Record<string, PostType | null>>({});
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // TikTok rejects posts without the user's explicit preview + publish consent.
  const [tiktokPrivacy, setTiktokPrivacy] = useState<string | null>(null);
  const [tiktokConsent, setTiktokConsent] = useState(false);

  // Prevent the browser from opening files dropped outside the drop zone
  // while the user is on this page.
  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault();
    window.addEventListener("dragover", prevent);
    window.addEventListener("drop", prevent);
    return () => {
      window.removeEventListener("dragover", prevent);
      window.removeEventListener("drop", prevent);
    };
  }, []);
  const [scheduleMode, setScheduleMode] = useState(!!prefill?.scheduledFor);
  const [scheduledFor, setScheduledFor] = useState(
    prefill?.scheduledFor
      ? toLocalInput(prefill.scheduledFor)
      : new Date(Date.now() + 3600_000).toISOString().slice(0, 16),
  );

  const togglePlatform = (p: SocialPlatform) => {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const uploadFile = async (rawFile: File) => {
    const type: "image" | "video" = rawFile.type.startsWith("video/") ? "video" : "image";

    // Show the loading state up front — video poster capture below can take a
    // few seconds, and it should read as "working", not a dead click.
    setUploading(true);
    try {
      let dims: { width: number; height: number } | null = null;
      if (type === "video") {
        const poster = await captureVideoPoster(rawFile);
        if (poster?.width && poster?.height) dims = { width: poster.width, height: poster.height };
      }

      // Optimize image client-side before upload (resize >1080 wide, PNG→JPEG)
      // so Instagram has less work to do and quality survives better.
      const { file, report } = await optimizeImageForUpload(rawFile);
      const note = describeOptimization(report);
      if (note) toast.success(note);

      const presigned = await apiFetch<{ uploadUrl: string; publicUrl: string }>(
        `/brands/${brand.id}/upload-url?filename=${encodeURIComponent(file.name)}&contentType=${encodeURIComponent(file.type)}`,
      );
      const res = await fetch(presigned.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!res.ok) throw new Error("Upload failed");
      setMediaUrl(presigned.publicUrl);
      setMediaType(type);
      setMediaDims(dims);
    } catch (err: any) {
      toast.error(err?.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
      toast.error(t("quickPost.mediaError.imageOrVideo"));
      return;
    }
    uploadFile(file);
  };

  const buildPayload = (withSchedule: boolean) => {
    const hashtags = hashtagsRaw
      .split(/[\s,]+/)
      .map((t) => t.replace(/^#/, "").trim())
      .filter(Boolean);
    return {
      caption: caption.trim(),
      hashtags,
      mediaUrls: mediaUrl ? [mediaUrl] : [],
      platforms: Array.from(selectedPlatforms).map((platform) => ({
        platform,
        postType: formatByPlatform[platform] ?? undefined,
        ...(platform === "TIKTOK"
          ? {
              tiktokSettings: {
                ...(tiktokPrivacy ? { privacyLevel: tiktokPrivacy } : {}),
                contentPreviewConfirmed: tiktokConsent,
                expressConsentGiven: tiktokConsent,
              },
            }
          : {}),
      })),
      ...(withSchedule ? { scheduledFor: new Date(scheduledFor).toISOString() } : {}),
    };
  };

  const mediaDescriptor: MediaDimensions | null = mediaUrl
    ? { kind: mediaType === "video" ? "video" : "image", width: mediaDims?.width, height: mediaDims?.height }
    : null;

  const mediaRequirements = Array.from(selectedPlatforms)
    .map((p) => ({ platform: p, requires: MEDIA_REQUIRED[p] }))
    .filter((r) => r.requires !== null);

  const mediaError: string | null = (() => {
    if (mediaRequirements.length === 0) return null;
    if (!mediaUrl) {
      const names = mediaRequirements.map((r) => r.platform.charAt(0) + r.platform.slice(1).toLowerCase()).join(", ");
      return `${names} require${mediaRequirements.length === 1 ? "s" : ""} media.`;
    }
    const videoOnly = mediaRequirements.filter((r) => r.requires === "video");
    if (videoOnly.length > 0 && mediaType !== "video") {
      const names = videoOnly.map((r) => r.platform.charAt(0) + r.platform.slice(1).toLowerCase()).join(", ");
      return `${names} require${videoOnly.length === 1 ? "s" : ""} a video.`;
    }
    const imageOnly = mediaRequirements.filter((r) => r.requires === "image");
    if (imageOnly.length > 0 && mediaType !== "image") {
      const names = imageOnly.map((r) => r.platform.charAt(0) + r.platform.slice(1).toLowerCase()).join(", ");
      return `${names} require${imageOnly.length === 1 ? "s" : ""} an image.`;
    }
    return null;
  })();

  const canSubmit =
    caption.trim().length > 0 &&
    selectedPlatforms.size > 0 &&
    !uploading &&
    !mediaError &&
    (!selectedPlatforms.has("TIKTOK") || tiktokConsent);

  const handlePostNow = () => {
    if (!canSubmit) return;
    if (!confirm(t("quickPost.confirmPostNow"))) return;
    quickPostMut.mutate(buildPayload(false), {
      onSuccess: () => navigate(`/${brandSlug}/calendar`),
    });
  };

  const handleSchedule = () => {
    if (!canSubmit) return;
    if (new Date(scheduledFor).getTime() < Date.now() + MIN_SCHEDULE_LEAD_MS) {
      toast.error(t("quickPost.scheduleTooSoon", { minutes: MIN_SCHEDULE_LEAD_MINUTES }));
      return;
    }
    quickPostMut.mutate(buildPayload(true), {
      onSuccess: () => navigate(`/${brandSlug}/calendar`),
    });
  };

  return (
    <div className="max-w-xl mx-auto py-8 px-6 space-y-6">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => navigate(`/${brandSlug}`)}
        >
          <ArrowLeft size={14} />
        </Button>
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Zap size={18} className="text-brand" />
            {t("quickPost.title")}
          </h1>
          <p className="text-xs text-muted-foreground">
            {t("quickPost.subtitle")}
          </p>
        </div>
      </div>

      {/* Caption */}
      <div className="space-y-1.5">
        <Label htmlFor="qp-caption">{t("quickPost.caption")}</Label>
        <Textarea
          id="qp-caption"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder={t("quickPost.captionPlaceholder")}
          className="min-h-[120px]"
        />
      </div>

      {/* Hashtags */}
      <div className="space-y-1.5">
        <Label htmlFor="qp-tags">{t("quickPost.hashtags")}</Label>
        <Input
          id="qp-tags"
          value={hashtagsRaw}
          onChange={(e) => setHashtagsRaw(e.target.value)}
          placeholder={t("quickPost.hashtagsPlaceholder")}
        />
      </div>

      {/* Media */}
      <div className="space-y-1.5">
        <Label>
          {t("quickPost.media")}{" "}
          {mediaRequirements.length > 0 ? (
            <span className="text-destructive">{t("quickPost.mediaRequired")}</span>
          ) : (
            <span className="text-muted-foreground font-normal">{t("quickPost.mediaOptional")}</span>
          )}
        </Label>
        {mediaUrl ? (
          <div className="relative rounded-md border border-border bg-surface-1 p-2">
            {mediaType === "video" ? (
              <video src={mediaUrl} controls className="max-h-48 mx-auto rounded" />
            ) : (
              <img src={mediaUrl} alt="" className="max-h-48 mx-auto rounded" />
            )}
            <button
              onClick={() => {
                setMediaUrl(null);
                setMediaType(null);
                setMediaDims(null);
                setFormatByPlatform({});
              }}
              className="absolute top-1 right-1 bg-surface-0 border border-border rounded p-1 hover:bg-surface-2"
              title={t("quickPost.remove")}
            >
              <X size={12} />
            </button>
          </div>
        ) : (
          <label
            onDragOver={(e) => {
              e.preventDefault();
              if (!dragOver) setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-md py-6 cursor-pointer transition-colors ${
              dragOver
                ? "border-brand bg-brand-subtle/30"
                : "border-border hover:bg-surface-1"
            }`}
          >
            <Upload size={18} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {uploading
                ? t("quickPost.uploading")
                : dragOver
                  ? t("quickPost.dropHint")
                  : t("quickPost.uploadHint")}
            </span>
            <input
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={handleFile}
              disabled={uploading}
            />
          </label>
        )}
        {!mediaUrl && (
          <div className="flex justify-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPickerOpen(true)}
              disabled={uploading}
            >
              {t("common:library.browse")}
            </Button>
          </div>
        )}
        {mediaError && <p className="text-xs text-destructive">{mediaError}</p>}
      </div>

      <LibraryPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        brandId={brand.id}
        multi={false}
        onConfirm={(_urls, assets) => {
          const a = assets[0];
          if (!a) return;
          const kind = assetToMediaKind(a.type);
          if (!kind) {
            toast.error(t("quickPost.mediaError.imageOrVideo"));
            return;
          }
          setMediaUrl(a.url);
          setMediaType(kind);
          setMediaDims(a.width && a.height ? { width: a.width, height: a.height } : null);
        }}
      />

      {/* Platforms */}
      <div className="space-y-1.5">
        <Label>{t("quickPost.platforms")}</Label>
        {activeAccounts.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t("quickPost.noAccounts")}{" "}
            <button
              onClick={() => navigate(`/${brandSlug}/settings/accounts`)}
              className="underline"
            >
              {t("quickPost.connectOne")}
            </button>{" "}
            {t("quickPost.connectFirst")}
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {activeAccounts.map((acc) => {
              const selected = selectedPlatforms.has(acc.platform);
              return (
                <button
                  key={acc.id}
                  type="button"
                  onClick={() => togglePlatform(acc.platform)}
                  className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[12px] transition-colors ${
                    selected
                      ? "border-brand bg-brand-subtle text-brand-subtle-text"
                      : "border-border hover:bg-surface-1"
                  }`}
                >
                  <span className="font-medium">
                    {acc.platform.charAt(0) + acc.platform.slice(1).toLowerCase()}
                  </span>
                  {acc.username && (
                    <span className="text-muted-foreground">@{acc.username}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {Array.from(selectedPlatforms).map((platform) => (
          <FormatPicker
            key={platform}
            platform={platform}
            media={mediaDescriptor}
            value={formatByPlatform[platform] ?? null}
            onChange={(pt) => setFormatByPlatform((prev) => ({ ...prev, [platform]: pt }))}
          />
        ))}
        {selectedPlatforms.has("TIKTOK") && (
          <div className="space-y-2 rounded-md border border-border bg-surface-1 p-3">
            <TikTokPrivacyKnob
              brandId={brand.id}
              value={tiktokPrivacy}
              onChange={setTiktokPrivacy}
              mediaType={mediaType === "image" ? "photo" : "video"}
            />
            <p className="text-[11px] text-muted-foreground">{t("quickPost.tiktokPrivacyHint")}</p>
            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={tiktokConsent}
                onChange={(e) => setTiktokConsent(e.target.checked)}
              />
              <span>{t("quickPost.tiktokConsent")}</span>
            </label>
            {!tiktokConsent && (
              <p className="text-[11px] text-destructive">{t("quickPost.tiktokConsentRequired")}</p>
            )}
          </div>
        )}
      </div>

      {/* Schedule toggle */}
      {scheduleMode && (
        <div className="space-y-1.5 rounded-md border border-border bg-surface-1 p-3">
          <Label htmlFor="qp-when" className="flex items-center gap-1.5">
            <CalendarClock size={13} /> {t("quickPost.scheduledFor")}
          </Label>
          <Input
            id="qp-when"
            type="datetime-local"
            value={scheduledFor}
            min={toLocalInput(new Date(Date.now() + MIN_SCHEDULE_LEAD_MS).toISOString())}
            onChange={(e) => setScheduledFor(e.target.value)}
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-4">
        {scheduleMode ? (
          <>
            <Button variant="ghost" onClick={() => setScheduleMode(false)}>
              {t("quickPost.cancelScheduling")}
            </Button>
            <Button
              onClick={handleSchedule}
              disabled={!canSubmit || quickPostMut.isPending}
            >
              <CalendarClock size={14} className="mr-1" />
              {quickPostMut.isPending ? t("quickPost.scheduling") : t("quickPost.scheduleAndSend")}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              onClick={() => setScheduleMode(true)}
              disabled={!canSubmit || quickPostMut.isPending}
            >
              <CalendarClock size={14} className="mr-1" />
              {t("quickPost.schedule")}
            </Button>
            <Button
              onClick={handlePostNow}
              disabled={!canSubmit || quickPostMut.isPending}
            >
              <Zap size={14} className="mr-1" />
              {quickPostMut.isPending ? t("quickPost.posting") : t("quickPost.postNow")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
